import React, { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
	Box,
	Button,
	Checkbox,
	FileTypeIcon,
	Input,
	Label,
	ModalBase,
} from '@meddihub/ui';
import type {
	HealthDocumentationFile,
	SaveToHealthDocumentationRequest,
	SaveToHealthDocumentationResult,
} from '@meddihub/mzcr-ui';
import apiClient from '../../../Services/ApiClient';
import { isApiSuccess } from '../../../Utils/http.util';
import { useDocumentationFoldersQuery } from '../../../graphql/graphql';
import useTranslation from '../../../Hooks/useTranslation';
import useNotification from '../../../Hooks/useNotification.hook';
import { base64ToBlob } from '../Utils/base64.util';
import { formatFilesCount, formatSize } from '../Utils/format.util';
import {
	pickInitialFolder,
	readLastFolders,
	rememberFolder,
} from '../Utils/healthDocumentationFolder.util';
// @ts-ignore
import styles from './SaveToHealthDocumentationModal.module.scss';
import ListSkeleton from '../../Base/Components/ListSkeleton';
import {
	CheckIcon,
	FolderIcon,
	FolderInputIcon,
	InfoIcon,
	PlusIcon,
} from './icons';

interface Props {
	patientId: string;
	request: SaveToHealthDocumentationRequest;
	/** `null` when the user closed the dialog without saving anything. */
	onClose: (result: SaveToHealthDocumentationResult) => void;
}

const fileFormat = (file: HealthDocumentationFile) =>
	(file.fileName.split('.').pop() ?? '').toUpperCase();

/**
 * Stores files from MZČR in the patient's health documentation – the user names the document
 * (a single file) or picks the files (several), and picks the folder (designs P3 / D6).
 */
const SaveToHealthDocumentationModal: React.FC<Props> = props => {
	const { request, onClose } = props;

	// Nothing to store - the service gets its answer right away instead of an empty dialog
	useEffect(() => {
		if (request.files.length === 0) {
			onClose(null);
		}
	}, []);

	return request.files.length === 0 ? null : (
		<SaveToHealthDocumentationDialog {...props} />
	);
};

const SaveToHealthDocumentationDialog: React.FC<Props> = ({
	patientId,
	request,
	onClose,
}) => {
	const { files, sourceName, suggestedFolderName } = request;
	const isSingle = files.length === 1;
	const kind = files[0].kind;

	const { t } = useTranslation();
	const { showNotificationSuccess, showNotificationAlert } = useNotification();
	const { data, isLoading, refetch } = useDocumentationFoldersQuery(
		{ userId: patientId },
		{ refetchOnMountOrArgChange: true },
	);
	const [name, setName] = useState(files[0].title ?? '');
	// Files are told apart by their position - two files of a package may have the same name
	const fileIndexes = Array.from({ length: files.length }, (_, index) => index);
	const [selectedIndexes, setSelectedIndexes] = useState<number[]>(fileIndexes);
	// Files already stored - a retry after a failure in the middle must not store them again
	const [savedIndexes, setSavedIndexes] = useState<number[]>([]);
	const [folderId, setFolderId] = useState<string | null>(null);
	const [newFolderName, setNewFolderName] = useState<string | null>(null);
	const [isSaving, setIsSaving] = useState(false);
	const [preselectedBy, setPreselectedBy] = useState<'last' | 'type' | null>(
		null,
	);

	const folders = useMemo(
		() =>
			(data?.documentationFolders?.data ?? []).filter(Boolean).map(folder => ({
				id: folder!.id as string,
				name: folder!.name ?? '',
				filesCount: folder!.filesCount ?? 0,
			})),
		[data],
	);

	// Preselect the folder used last time, or the one named after the kind / type of the document
	useEffect(() => {
		if (folderId) {
			return;
		}
		const initial = pickInitialFolder({
			folders,
			kind,
			suggestedFolderName,
			lastFolders: readLastFolders(),
			t,
		});
		if (initial) {
			setFolderId(initial.folderId);
			setPreselectedBy(initial.by);
		}
	}, [folders, folderId, kind, suggestedFolderName, t]);

	const selectedFolder = folders.find(folder => folder.id === folderId);
	// The folder is fixed once something is stored in it - the result names one folder
	const isFolderLocked = savedIndexes.length > 0;
	const pendingIndexes = fileIndexes.filter(
		index => !savedIndexes.includes(index),
	);
	const indexesToSave = isSingle
		? pendingIndexes
		: pendingIndexes.filter(index => selectedIndexes.includes(index));

	const selectFolder = (id: string) => {
		setFolderId(id);
		setPreselectedBy(null);
	};

	const toggleFile = (index: number, checked: boolean) =>
		setSelectedIndexes(current =>
			checked
				? [...current, index]
				: current.filter(selected => selected !== index),
		);

	const handleCreateFolder = async () => {
		const folderName = newFolderName?.trim();
		if (!folderName) {
			return;
		}
		const id = uuidv4();
		try {
			const response = await apiClient.post(
				`/patients/${patientId}/documentation/${id}`,
				{ name: folderName },
			);
			if (!isApiSuccess(response)) {
				throw new Error('Folder could not be created');
			}
			setNewFolderName(null);
			await refetch();
			selectFolder(id);
		} catch (e) {
			console.error(e);
			showNotificationAlert({
				content: t('component.mzcr.saveModal.folderCreateFailed'),
			});
		}
	};

	const uploadFile = async (file: HealthDocumentationFile, title: string) => {
		const url = `/patients/${patientId}/documentation/${
			selectedFolder!.id
		}/${uuidv4()}`;
		const record = await apiClient.post(url, {
			folderId: selectedFolder!.id,
			name: title,
		});
		if (!isApiSuccess(record)) {
			throw new Error('Document record could not be created');
		}

		const formData = new FormData();
		formData.append(
			'files[]',
			new File(
				[base64ToBlob(file.contentBase64, file.mimeType)],
				file.fileName,
				{
					type: file.mimeType,
				},
			),
		);
		const upload = await apiClient.postMultipart(`${url}/upload`, formData);
		if (!isApiSuccess(upload)) {
			// Do not leave an empty document in the folder - the retry creates it again
			await apiClient.delete(url);
			throw new Error('File could not be uploaded');
		}
	};

	const resultOf = (indexes: number[]): SaveToHealthDocumentationResult =>
		indexes.length > 0 && selectedFolder
			? {
					folderName: selectedFolder.name,
					fileNames: indexes.map(index => files[index].fileName),
			  }
			: null;

	const handleSave = async () => {
		if (!selectedFolder || indexesToSave.length === 0) {
			return;
		}
		if (isSingle && !name.trim()) {
			return;
		}
		setIsSaving(true);
		const saved = [...savedIndexes];
		try {
			for (const index of indexesToSave) {
				await uploadFile(
					files[index],
					isSingle ? name.trim() : files[index].title,
				);
				saved.push(index);
			}
			rememberFolder(kind, selectedFolder.id);
			showNotificationSuccess({
				content: isSingle
					? t('component.mzcr.saveModal.savedSingle', {
							folder: selectedFolder.name,
					  })
					: t('component.mzcr.saveModal.savedMultiple', {
							files: formatFilesCount(saved.length, t),
							folder: selectedFolder.name,
					  }),
			});
			onClose(resultOf(saved));
		} catch (e) {
			console.error(e);
			setSavedIndexes(saved);
			showNotificationAlert({
				content:
					saved.length > 0
						? t('component.mzcr.saveModal.savedPartially', {
								saved: String(saved.length),
								total: String(files.length),
						  })
						: t('component.mzcr.saveModal.saveFailed'),
			});
		} finally {
			setIsSaving(false);
		}
	};

	const canSave =
		!!selectedFolder &&
		indexesToSave.length > 0 &&
		(!isSingle || !!name.trim());

	const totalCount = folders.reduce(
		(sum, folder) => sum + folder.filesCount,
		0,
	);

	return (
		<ModalBase
			open
			// What is already stored stays stored - the service learns about it
			onClose={() => onClose(resultOf(savedIndexes))}
			size='md'
			className={styles.modal}>
			<ModalBase.Header
				className={styles.header}
				title={
					isSingle
						? t('component.mzcr.saveModal.titleSingle')
						: t('component.mzcr.saveModal.titleMultiple')
				}
			/>
			<ModalBase.Body maxHeight className={styles.body}>
				{isSingle ? (
					<>
						<div className={styles.filePreview}>
							<FileTypeIcon
								mimeType={files[0].mimeType}
								fileName={files[0].fileName}
								size='lg'
							/>
							<div className={styles.texts}>
								<span className={styles.fileName}>{files[0].fileName}</span>
								<span className={styles.meta}>
									{files[0].source} · {formatSize(files[0].size)}
								</span>
							</div>
						</div>
						<div className={styles.field}>
							<Label htmlFor='mzcr-document-name'>
								{t('component.mzcr.saveModal.documentName')}
							</Label>
							<Input
								id='mzcr-document-name'
								value={name}
								hasError={!name.trim()}
								onChange={event => setName(event.target.value)}
							/>
						</div>
					</>
				) : (
					<div className={styles.field}>
						<div className={styles.labelRow}>
							<Label>{t('component.mzcr.saveModal.filesToSave')}</Label>
							{sourceName && (
								<span className={styles.source}>
									{t('component.mzcr.saveModal.sourceName', {
										name: sourceName,
									})}
								</span>
							)}
						</div>
						<ul className={styles.fileList}>
							{pendingIndexes.map(index => {
								const file = files[index];
								return (
									<li key={index} className={styles.fileRow}>
										<Checkbox
											aria-label={t('component.mzcr.saveModal.saveFile', {
												name: file.fileName,
											})}
											checked={selectedIndexes.includes(index)}
											onChange={checked => toggleFile(index, checked)}
										/>
										<FileTypeIcon
											mimeType={file.mimeType}
											fileName={file.fileName}
											size='sm'
										/>
										<div className={styles.texts}>
											<span className={styles.fileName}>{file.fileName}</span>
											<span className={styles.meta}>
												{fileFormat(file)} · {formatSize(file.size)}
											</span>
										</div>
									</li>
								);
							})}
						</ul>
						{isFolderLocked && (
							<p className={styles.hint}>
								<InfoIcon />
								{selectedFolder
									? t('component.mzcr.saveModal.savedHint', {
											saved: String(savedIndexes.length),
											total: String(files.length),
											folder: selectedFolder.name,
									  })
									: t('component.mzcr.saveModal.savedHintNoFolder', {
											saved: String(savedIndexes.length),
											total: String(files.length),
									  })}
							</p>
						)}
					</div>
				)}

				<div className={styles.field}>
					<div className={styles.labelRow}>
						<Label id='mzcr-folder-label'>
							{t('component.mzcr.saveModal.folder')}
						</Label>
						{newFolderName === null && !isFolderLocked && (
							<button
								type='button'
								className={styles.newFolder}
								onClick={() => setNewFolderName('')}>
								{t('component.mzcr.saveModal.newFolder')}
								<span className={styles.newFolderPlus}>
									<PlusIcon size={10} />
								</span>
							</button>
						)}
					</div>

					{newFolderName !== null && (
						<Box flex={{ direction: 'row', gap: 3, align: 'center' }}>
							<Input
								aria-label={t('component.mzcr.saveModal.newFolderName')}
								placeholder={t('component.mzcr.saveModal.newFolderName')}
								autoFocus
								value={newFolderName}
								onChange={event => setNewFolderName(event.target.value)}
								onKeyDown={event =>
									event.key === 'Enter' && handleCreateFolder()
								}
							/>
							<Button
								variant='outline'
								disabled={!newFolderName.trim()}
								onClick={handleCreateFolder}>
								{t('component.mzcr.saveModal.createFolder')}
							</Button>
							<Button variant='link' onClick={() => setNewFolderName(null)}>
								{t('component.mzcr.saveModal.cancel')}
							</Button>
						</Box>
					)}

					{isLoading ? (
						<ListSkeleton rows={4} compact />
					) : (
						<ul
							className={styles.folders}
							role='radiogroup'
							aria-labelledby='mzcr-folder-label'>
							<li className={`${styles.folder} ${styles.folderRoot}`}>
								<FolderIcon open />
								<span className={styles.folderName}>
									{t('component.mzcr.healthDocumentation')}
								</span>
								<span className={styles.count}>
									{formatFilesCount(totalCount, t)}
								</span>
							</li>
							{folders.map(folder => {
								const isSelected = folder.id === folderId;
								return (
									<li key={folder.id}>
										<button
											type='button'
											role='radio'
											aria-checked={isSelected}
											disabled={isFolderLocked}
											className={`${styles.folder}${
												isSelected ? ` ${styles.folderSelected}` : ''
											}`}
											onClick={() => selectFolder(folder.id)}>
											<FolderIcon />
											<span className={styles.folderName}>{folder.name}</span>
											<span className={styles.count}>
												{formatFilesCount(folder.filesCount, t)}
											</span>
											<span className={styles.radio}>
												{isSelected && <CheckIcon />}
											</span>
										</button>
									</li>
								);
							})}
						</ul>
					)}
					{preselectedBy && (
						<p className={styles.hint}>
							<InfoIcon />
							{preselectedBy === 'last'
								? t('component.mzcr.saveModal.preselectedLast')
								: t('component.mzcr.saveModal.preselectedType')}
						</p>
					)}
				</div>
			</ModalBase.Body>
			<ModalBase.Footer className={styles.footer}>
				<Button
					variant='success'
					icon={<FolderInputIcon />}
					disabled={!canSave}
					isLoading={isSaving}
					onClick={handleSave}>
					{isSingle
						? t('component.mzcr.saveModal.saveToFolder')
						: t('component.mzcr.saveModal.saveFilesToFolder', {
								files: formatFilesCount(indexesToSave.length, t),
						  })}
				</Button>
				{selectedFolder && (
					<span className={styles.note}>
						{isSingle
							? t('component.mzcr.saveModal.noteSingle', {
									folder: selectedFolder.name,
							  })
							: t('component.mzcr.saveModal.noteMultiple')}
					</span>
				)}
			</ModalBase.Footer>
		</ModalBase>
	);
};

export default SaveToHealthDocumentationModal;
